import type { ReactNode } from "react";
import type { Tone } from "@onecpa/shared";

/**
 * The single status chip for the entire product.
 *
 * Every status vocabulary in @onecpa/shared maps onto these six tones, and no
 * screen is permitted to pick a colour directly. That is what keeps the palette's
 * two laws intact: red means money lost (penalty, loss, error) and ochre means
 * late — never decoration.
 *
 * Wash background plus ink text, so it reads as a state chip rather than as
 * coloured text.
 *
 * Metrics matter as much as colour here. An arbitrary `text-2xs` sets the font
 * size and nothing else, so the chip inherited the document's 1.5 line-height:
 * the box grew to the line box rather than the text, and Hebrew — which has no
 * descenders to balance the ascent — sat visibly low inside it. A fixed height
 * with `leading-none` centres the glyphs on the chip's own axis instead.
 */
const TONES: Record<Tone, string> = {
  neutral: "bg-wash text-ink-muted",
  info: "bg-info-bg text-info-ink",
  success: "bg-success-bg text-success-ink",
  warning: "bg-warning-bg text-warning-ink",
  danger: "bg-danger-bg text-danger-ink",
  pending: "bg-pending-bg text-pending-ink",
};

export interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}

export function Badge({ tone = "neutral", children, className = "" }: BadgeProps) {
  return (
    <span
      className={`inline-flex h-5 items-center gap-1.5 rounded-pill px-2 text-2xs leading-none font-semibold whitespace-nowrap ${TONES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * A count beside a label: tab counts, nav badges, the notification bubble.
 *
 * Extracted because six of these were hand-rolled with different metrics —
 * several with no vertical padding at all, so their height came from the line box
 * and they sat off the baseline of the text they annotate. One square-ish pill
 * with tabular figures, so a two-digit count does not shift its neighbours.
 */
export type CountTone = "neutral" | "accent" | "chrome" | "danger";

const COUNT_TONES: Record<CountTone, string> = {
  neutral: "bg-wash text-ink-faint",
  accent: "bg-primary-soft text-ledger",
  // On the ink rail, where the surface is dark in both themes.
  chrome: "bg-white/10 text-on-ink",
  // Paper-coloured text tracks the theme, so it stays legible when red lightens.
  danger: "bg-red-ink text-paper",
};

export function CountPill({
  count,
  tone = "neutral",
  className = "",
}: {
  count: number;
  /** Counts above this read as "a lot"; the exact figure stops being useful. */
  tone?: CountTone;
  className?: string;
}) {
  return (
    <span
      className={`tnum inline-flex h-4 min-w-4 items-center justify-center rounded-pill px-1 text-2xs leading-none font-semibold ${COUNT_TONES[tone]} ${className}`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
